<!-- @format -->

<ng-template #modalContent>

  <output
    class="sr-only"
    aria-live="assertive"
    aria-atomic="true">
    @if (hasMultipleImages()) {
      {{ imageCountLabel() }}
    }
  </output>

  <output
    class="sr-only"
    aria-live="polite"
    aria-atomic="true">
    {{ zoomAnnouncement() }}
  </output>

  <section
    class="gallery-container hmf-display-flex hmf-p-xs hmf-p-m-s hmf-gap-s"
    [class.gallery-container-mobile]="useStackedLayout()"
    [class.gallery-container-desktop]="!useStackedLayout()"
    [class.hmf-flex-column]="useStackedLayout()"
    [class.hmf-flex-row]="!useStackedLayout()"
    [attr.aria-label]="'Image gallery with ' + (mediaItems()?.length || 0) + ' items'">
    <figure class="gallery-main-image hmf-display-flex hmf-align-items-center hmf-justify-content-center hmf-position-relative">
      <!-- Homefield positions snackbars against the viewport; gallery styles center this one over the image. -->
      <hmf-snackbar
        #zoomHintSnackbar
        class="gallery-zoom-hint-snackbar"
        [type]="'light'"
        [action]="'auto-dismiss'"
        [dismissDelay]="zoomHintDismissDelaySeconds()"
        [showLeftIcon]="false">
        {{ zoomHintMessage() }}
      </hmf-snackbar>

      @if (mediaItems().length > 0) {
        <div
          #swipeAreaRef
          class="gallery-image-wrapper hmf-display-flex hmf-align-items-center hmf-justify-content-center hmf-w-100 hmf-position-relative"
          (touchstart)="onSwipeStart($event)"
          (touchend)="onSwipeEnd($event)"
          (touchcancel)="onSwipeCancel()">
          <!-- Keep neighboring slides mounted for swipe transitions; this unpadded viewport clips them. -->
          <div class="gallery-viewport">
            <div
              class="gallery-track"
              [class.is-dragging]="isDragging() || skipTrackTransition()"
              [style.transform]="trackTransform()">
              @for (item of mediaItems(); track $index; let slideIndex = $index) {
                <div class="gallery-slide">
                  <div class="gallery-image-content hmf-display-flex hmf-align-items-center hmf-justify-content-center hmf-w-100 hmf-position-relative">
                    @if (item.type === 'video') {
                      @if (slideIndex === activeIndex()) {
                        <shared-media-component-video
                          class="gallery-main-video hmf-display-block"
                          [animation]="activeVideoUrl() ?? undefined"
                          [image]="item.poster"
                          [altText]="productName() + ' product video'"
                          [autoplay]="true"
                          [showControls]="true"
                          [useScene7URLs]="false"
                          [controlsList]="'nodownload'">
                        </shared-media-component-video>
                      } @else {
                        <!-- Posters avoid mounting and buffering inactive video players. -->
                        <img
                          class="gallery-main-video hmf-display-block"
                          draggable="false"
                          [src]="posterUrlFor(item)"
                          [alt]="productName() + ' product video'"
                          aria-hidden="true"
                          loading="lazy"
                          decoding="async" />
                      }
                    } @else {
                      @if (isSlideInWindow(slideIndex)) {
                        <div
                          #zoomAreaRef
                          class="gallery-zoom-area hmf-w-100 hmf-h-100 hmf-display-flex hmf-align-items-center hmf-justify-content-center hmf-position-relative"
                          [attr.data-slide-index]="slideIndex"
                          [attr.role]="slideIndex === activeIndex() ? 'button' : null"
                          [attr.tabindex]="slideIndex === activeIndex() ? 0 : -1"
                          [attr.aria-hidden]="slideIndex === activeIndex() ? null : true"
                          [attr.inert]="slideIndex === activeIndex() ? null : ''"
                          [class.gallery-zoom-in-cursor]="slideIndex === activeIndex() && zoomCursorClass() === 'gallery-zoom-in-cursor'"
                          [class.gallery-zoom-out-cursor]="slideIndex === activeIndex() && zoomCursorClass() === 'gallery-zoom-out-cursor'"
                          [attr.aria-label]="slideIndex === activeIndex() ? zoomAriaLabel() : null"
                          [attr.aria-pressed]="slideIndex === activeIndex() ? isZoomed() : null"
                          [attr.aria-disabled]="slideIndex === activeIndex() ? isZoomLocked() : null"
                          (click)="onZoomClick()"
                          (keydown)="onZoomKeydown($event)"
                          (pointermove)="onZoomPointerMove($event)"
                          (touchstart)="onZoomTouchStart($event)"
                          (touchend)="onZoomTouchEnd($event)">
                          <picture
                            #mainImageRef
                            class="gallery-main-img hmf-display-block"
                            [attr.data-slide-index]="slideIndex"
                            [class.gallery-swiping]="slideIndex === activeIndex() && isSwiping()">
                            <img
                              [class.gallery-main-image-loading]="isMainImageLoading(item)"
                              draggable="false"
                              [src]="mainImageSrc(item)"
                              [attr.srcset]="item.srcset || null"
                              [attr.sizes]="mainImageSizes()"
                              [attr.fetchpriority]="slideIndex === activeIndex() ? 'high' : 'auto'"
                              [alt]="productName()"
                              [title]="productName()"
                              width="2000"
                              height="2000"
                              loading="eager"
                              decoding="async"
                              itemprop="image"
                              (error)="handleMainImageError($event)"
                              sharedUtilsDirectiveImageLoadingStatus
                              [loadingKey]="mainImageSrc(item)"
                              [loadingActive]="isOpen()"
                              (loadingStatusChange)="setMainImageLoadingStatus(item, $event)" />
                          </picture>
                          @if (isMainImageDelayed(item)) {
                            <shared-component-loading-spinner
                              class="gallery-image-loading-spinner hmf-position-absolute"
                              role="status"
                              aria-label="Loading image" />
                          }
                        </div>
                      }
                    }
                  </div>
                </div>
              }
            </div>
          </div>

          @if (!isMobile() && hasMultipleImages() && canGoPrevious()) {
            <button
              hmf-button
              type="button"
              class="gallery-nav-btn gallery-nav-prev hmf-position-absolute"
              variant="secondary"
              size="medium"
              shape="circular"
              icon="chevron-left-outline"
              [label]="'Previous image'"
              (click)="goToPrevious()">
            </button>
          }

          @if (!isMobile() && hasMultipleImages() && canGoNext()) {
            <button
              hmf-button
              type="button"
              class="gallery-nav-btn gallery-nav-next hmf-position-absolute"
              variant="secondary"
              size="medium"
              shape="circular"
              icon="chevron-right-outline"
              [label]="'Next image'"
              (click)="goToNext()">
            </button>
          }
        </div>
      }
    </figure>

    @if (hasMultipleImages()) {
      <nav
        class="gallery-thumbnails"
        [class.hmf-pt-m]="!useStackedLayout()"
        [class.gallery-thumbnails-mobile]="useStackedLayout()"
        [class.gallery-thumbnails-desktop]="!useStackedLayout()"
        [attr.aria-label]="'Thumbnail navigation'"
        [style.--gallery-thumbnail-size]="selectableSizeByBreakpoint()"
        sharedUtilsDirectiveScrollGradientX
        [fadeWidth]="selectableSizeByBreakpoint()"
        [enabled]="useStackedLayout()">
        <hmf-selectable-group
          [options]="thumbnailOptions()"
          [value]="activeIndexValue()"
          [multipleSelection]="false"
          [allowToggle]="false"
          [type]="'outline'"
          [shape]="'rectangle'"
          [width]="selectableSizeByBreakpoint()"
          [height]="selectableSizeByBreakpoint()"
          [spaceBetween]="thumbnailGap()"
          [ariaLabel]="'Thumbnail navigation'"
          [contentTemplate]="thumbnailTemplate"
          (valueChange)="onThumbnailSelectionChange($event)" />

        <ng-template
          #thumbnailTemplate
          let-option>
          @if (isThumbnailLoading()(option.index)) {
            <div class="gallery-thumbnail-skeleton hmf-position-absolute" aria-hidden="true"></div>
          }

          <span
            class="gallery-thumbnail-image hmf-display-flex hmf-align-items-center hmf-justify-content-center"
            [attr.data-thumbnail-index]="option.index">
            <img
              class="gallery-thumbnail-img hmf-display-block"
              draggable="false"
              [src]="option.imageUrl"
              [alt]="option.imageAlt"
              loading="lazy"
              decoding="async"
              (load)="onThumbnailLoad(option.index)"
              (error)="onThumbnailLoad(option.index)" />
          </span>

          @if (option.isVideo) {
            <span class="gallery-thumbnail-video-indicator hmf-position-absolute" aria-hidden="true">
              <common-components-play-button [invertColors]="true"></common-components-play-button>
            </span>
          }
        </ng-template>
      </nav>
    }
  </section>
</ng-template>
